import {
  adSizesMap,
  GPT_COMPONENT_ID,
  GPT_GLOBAL_PROPERTIES,
  SIZE_MAPPING_NAMES,
} from '@/features/advertisement/constants/adSlots'
import { type AdConfig, type GptTargeting } from '@/features/advertisement/types/adConfig'
import {
  flattenGptArray,
  generateAdUnitPath,
  shouldDisableLazyLoad,
} from '@/features/advertisement/utils/globalOptimizationTool'

export const DESKTOP_SLOTS_SRP = [
  GPT_COMPONENT_ID.TOP_LEADERBOARD,
  GPT_COMPONENT_ID.INLINE_1,
  GPT_COMPONENT_ID.INLINE_2,
  GPT_COMPONENT_ID.INLINE_3,
  GPT_COMPONENT_ID.INLINE_4,
  GPT_COMPONENT_ID.MINI_LEADERBOARD,
  GPT_COMPONENT_ID.BASE_LEADERBOARD,
  GPT_COMPONENT_ID.SKYSCRAPER,
  GPT_COMPONENT_ID.SKYSCRAPER_UPPER_RIGHT,
  GPT_COMPONENT_ID.SKYSCRAPER_LOWER_LEFT,
]

export const DESKTOP_SLOTS_ZSRP = [
  GPT_COMPONENT_ID.TOP_LEADERBOARD,
  GPT_COMPONENT_ID.BASE_LEADERBOARD,
  GPT_COMPONENT_ID.SKYSCRAPER,
  GPT_COMPONENT_ID.SKYSCRAPER_UPPER_RIGHT,
  GPT_COMPONENT_ID.SKYSCRAPER_LOWER_LEFT,
]

export const MOBILE_SLOTS_SRP = [
  GPT_COMPONENT_ID.INLINE_1,
  GPT_COMPONENT_ID.INLINE_2,
  GPT_COMPONENT_ID.INLINE_3,
  GPT_COMPONENT_ID.INLINE_4,
  GPT_COMPONENT_ID.MINI_LEADERBOARD,
  GPT_COMPONENT_ID.BASE_LEADERBOARD,
  GPT_COMPONENT_ID.MWEB_LEADERBOARD,
]

export const MOBILE_SLOTS_ZSRP = [
  GPT_COMPONENT_ID.BASE_LEADERBOARD,
  GPT_COMPONENT_ID.MWEB_LEADERBOARD,
]

const adSlotConfig = {
  mweb: {
    zsrp: MOBILE_SLOTS_ZSRP,
    srp: MOBILE_SLOTS_SRP,
  },
  desktop: {
    zsrp: DESKTOP_SLOTS_ZSRP,
    srp: DESKTOP_SLOTS_SRP,
  },
}

export const SLOT_TARGETING_VALUES = {
  [GPT_COMPONENT_ID.TOP_LEADERBOARD]: { pos: 'top' },
  [GPT_COMPONENT_ID.INLINE_1]: { pos: 'bottom' },
  [GPT_COMPONENT_ID.INLINE_2]: { pos: 'bottom2' },
  [GPT_COMPONENT_ID.INLINE_3]: { pos: 'bottom3' },
  [GPT_COMPONENT_ID.INLINE_4]: { pos: 'bottom4' },
  [GPT_COMPONENT_ID.MINI_LEADERBOARD]: {}, // no values for mini leaderboard
  [GPT_COMPONENT_ID.BASE_LEADERBOARD]: { pos: 'base' },
  [GPT_COMPONENT_ID.SKYSCRAPER]: { pos: 'top' },
  [GPT_COMPONENT_ID.SKYSCRAPER_UPPER_RIGHT]: { pos: 'top-right' },
  [GPT_COMPONENT_ID.SKYSCRAPER_LOWER_LEFT]: { pos: 'bottom-left' },
  [GPT_COMPONENT_ID.MWEB_LEADERBOARD]: { pos: 'top' },
}

const generateAdSlots = (slotIds: string[], isDesktopMapView: boolean) => {
  return slotIds.map((slotId: string) => {
    // inline ids are appended with a number, but the ad sizes & sizeMappings are the same
    const isInlineAd = slotId.includes('inline')
    let sizeMappingName = SIZE_MAPPING_NAMES[slotId]
    let adSizes = adSizesMap[slotId]

    if (isInlineAd) {
      // inline ads have different size mappings for desktop map SRP view
      const INLINE_MAPPING_NAME = isDesktopMapView
        ? GPT_COMPONENT_ID.INLINE_MAP_SRP
        : GPT_COMPONENT_ID.INLINE

      sizeMappingName = SIZE_MAPPING_NAMES[INLINE_MAPPING_NAME]
      adSizes = adSizesMap[INLINE_MAPPING_NAME]
    }

    return {
      id: slotId,
      metaData: {},
      ...(shouldDisableLazyLoad(slotId) ? { enableLazyLoad: false } : {}),
      sizeMappingName,
      sizes: adSizes,
      targeting: SLOT_TARGETING_VALUES[slotId],
    }
  })
}

export const createSrpGptConfig = ({
  gptTargeting,
  isDesktopMapView = false,
}: {
  gptTargeting: GptTargeting[]
  isDesktopMapView?: boolean
}) => {
  const flattenedGptArray = flattenGptArray(gptTargeting)
  const { dv, pagetype } = flattenedGptArray

  const device = dv === 'm' ? 'mweb' : 'desktop'
  const deviceConfig = adSlotConfig[device]

  const simplifiedPageType = pagetype === 'zsrp' ? 'zsrp' : 'srp'
  const slotIds = deviceConfig[simplifiedPageType] || deviceConfig.srp

  const srpGptConfig: AdConfig = {
    ...GPT_GLOBAL_PROPERTIES(),
    path: generateAdUnitPath(flattenedGptArray),
    targeting: flattenedGptArray,
    slots: generateAdSlots(slotIds, isDesktopMapView),
  }

  return srpGptConfig
}
